import { forEachWithGitYield } from '../../git/git-runtime.js';
import { gitResetWorktreeToBranch } from '../../git/worktrees/reset-worktree-to-branch.js';
import type { LogFn } from '../../log.js';
import type { PreviewWorktreeManager } from '../manager/preview-worktree-manager.js';
import { applyWorkingTreeWip } from './capture-and-apply-wip.js';
import { resolveIsolatedCheckoutForRepoPathRelativeToWorkspaceRoot } from './resolve-isolated-checkout-for-repo-path-relative-to-workspace-root.js';
import type { SessionRepoDeployState } from './types.js';

export async function applySessionWipToPreviewCheckouts(options: {
  previewWorktreeManager: PreviewWorktreeManager;
  environmentId: string;
  deployBranch: string;
  bridgeRoot: string;
  repoStates: SessionRepoDeployState[];
  log: LogFn;
}): Promise<{ ok: true } | { ok: false; error: string }> {
  const { previewWorktreeManager, environmentId, deployBranch, bridgeRoot, repoStates, log } = options;
  const eid = environmentId.trim();
  const checkoutPaths = previewWorktreeManager.getRepoCheckoutPaths(eid) ?? [];
  const sessionParentPath = previewWorktreeManager.getPreviewParentPath(eid) ?? null;

  let failure: { ok: false; error: string } | null = null;
  await forEachWithGitYield(repoStates, async (state) => {
    if (failure) return;
    const previewCheckout = await resolveIsolatedCheckoutForRepoPathRelativeToWorkspaceRoot({
      checkoutPaths,
      sessionParentPath,
      bridgeRoot,
      repoPathRelativeToWorkspaceRoot: state.repoPathRelativeToWorkspaceRoot,
    });
    if (!previewCheckout) {
      failure = {
        ok: false,
        error: `Preview worktree missing for ${state.repoPathRelativeToWorkspaceRoot}`,
      };
      return;
    }
    await gitResetWorktreeToBranch(previewCheckout, deployBranch);
    const applied = await applyWorkingTreeWip(
      previewCheckout,
      state.sessionCheckout,
      state.wip,
      log,
    );
    if (!applied.ok) {
      failure = {
        ok: false,
        error:
          applied.error ??
          `Failed to apply session WIP to preview (${state.repoPathRelativeToWorkspaceRoot})`,
      };
    }
  });

  return failure ?? { ok: true };
}
